import {Component, EventEmitter, inject, Input, Output} from '@angular/core';
import {Router} from '@angular/router';
import {CommonModule} from '@angular/common';
import {PrimeNgImportsModule} from '../../prime-ng-imports';
import {IBaseComponent} from '../ibase/ibase.component';
import {AutoRefreshInterval, AutoRefreshService} from '../../services/auto-refresh.service';

@Component({
    selector: 'app-auto-refresh',
    standalone: true,
    imports: [CommonModule, PrimeNgImportsModule],
    templateUrl: './auto-refresh.component.html',
    styleUrl: './auto-refresh.component.scss'
})
export class AutoRefreshComponent extends IBaseComponent {
    private readonly router: Router = inject(Router);
    private instanceIdValue?: string;
    private defaultIntervalValue = AutoRefreshInterval.FIVE_MINUTES;

    // ===============================================================
    // ===============================================================

    /**
     * Unique identifier for this auto-refresh instance
     * Used to manage multiple auto-refresh components independently
     * If not provided, will automatically generate a stable ID based on the current route
     */
    @Input()
    get instanceId(): string {
        if (!this.instanceIdValue) {
            this.instanceIdValue = this.generateRouteBasedInstanceId();
        }
        return this.instanceIdValue;
    }
    set instanceId(value: string) {
        if (!value || value.trim().length === 0) {
            throw new Error('instanceId cannot be empty or whitespace');
        }
        this.instanceIdValue = value.trim();
    }

    /**
     * Default refresh interval in minutes when component initializes
     * Must be a valid AutoRefreshInterval value
     */
    @Input()
    get defaultInterval(): number {
        return this.defaultIntervalValue;
    }
    set defaultInterval(value: number) {
        const validIntervals = Object.values(AutoRefreshInterval);
        if (!validIntervals.includes(value)) {
            throw new Error(`defaultInterval must be one of: ${validIntervals.join(', ')}`);
        }
        this.defaultIntervalValue = value;
    }

    /**
     * Whether the auto-refresh controls are disabled
     */
    @Input() disabled = false;

    /**
     * Whether to show the manual refresh button
     */
    @Input() showRefreshButton = false;

    /**
     * Event emitted when refresh is triggered (manual or automatic)
     */
    @Output() refresh = new EventEmitter<void>();

    // ===============================================================
    // ===============================================================

    protected currentInterval = AutoRefreshInterval.FIVE_MINUTES;
    protected intervalOptions: {label: string; value: number}[] = [];
    protected isActive = false;

    // ===============================================================
    // ===============================================================

    /**
     * On ready (Error handling and refresh is done in the parent)
     */
    protected override async onReady(): Promise<void> {
        this.intervalOptions = AutoRefreshService.getAvailableIntervals();

        // Register this instance with the service
        AutoRefreshService.registerInstance(this.instanceId, () => this.handleRefresh(), this.defaultInterval);

        // Get current configuration
        const config = AutoRefreshService.getInstanceConfig(this.instanceId);
        if (config) {
            this.currentInterval = config.intervalMinutes;
            this.isActive = config.isActive;
        }
    }

    /**
     * On destroy (Error handling and refresh is done in the parent)
     */
    protected override async onDestroy(): Promise<void> {
        // Unregister this instance from the service
        AutoRefreshService.unregisterInstance(this.instanceId);
    }

    // ===============================================================
    // ===============================================================

    /**
     * Handle interval change from dropdown
     * @param event Dropdown change event
     */
    protected onIntervalChange(event: {value: number}): void {
        const newInterval = event.value;
        this.currentInterval = newInterval;
        this.isActive = newInterval > AutoRefreshInterval.OFF;

        AutoRefreshService.updateInterval(this.instanceId, newInterval);
    }

    /**
     * Handle manual refresh button click
     */
    protected manualRefresh(): void {
        if (this.disabled) return;
        this.refresh.emit();
    }

    // ===============================================================
    // ===============================================================

    /**
     * Handle refresh triggered by the service
     */
    private handleRefresh(): void {
        if (this.disabled) return;
        this.refresh.emit();
    }

    // ===============================================================
    // ===============================================================

    /**
     * Generate a stable auto-refresh instance ID based on the current route
     * This ensures that auto-refresh preferences persist across page navigations
     */
    private generateRouteBasedInstanceId(): string {
        const currentUrl = this.router.url;

        const cleanUrl = currentUrl
            .split('?')[0]
            .split('#')[0]
            .replace(/^\//, '')
            .replace(/\/$/, '')
            .replaceAll('/', '-');

        return cleanUrl;
    }
}
